import { useHistory } from "react-router-dom";
import { useWebSocket } from "@circle-react/hooks/useWebSocket";
import type { ConversationChannel } from "../types/ConversationChannel";
import type { ConversationStatus } from "../types/ConversationStatus";
import type { HelpWidgetConversation } from "../types/help_widget_conversation";
import type { HelpWidgetMessage } from "../types/help_widget_message";
import { appendMessageWithFadingEffect } from "../utils/messageHelpers";

interface UseConversationWebSocketParams {
  conversationId: string;
  isVirtualConversation: boolean;
  markAsRead: (params: { conversationId: string }) => void;
  scrollToBottom: () => void;
  setChannel: React.Dispatch<React.SetStateAction<ConversationChannel | null>>;
  setCopilotLoadingText: React.Dispatch<React.SetStateAction<string | null>>;
  setIsBotProcessing: React.Dispatch<React.SetStateAction<boolean>>;
  setIsWaitingForConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
  setIsWaitingInQueue: React.Dispatch<React.SetStateAction<boolean>>;
  setMessages: React.Dispatch<React.SetStateAction<HelpWidgetMessage[]>>;
  setQueuePosition: React.Dispatch<React.SetStateAction<number | null>>;
  setStatus: React.Dispatch<React.SetStateAction<ConversationStatus | null>>;
}

export const useConversationWebSocket = ({
  conversationId,
  isVirtualConversation,
  setMessages,
  setChannel,
  setStatus,
  setIsBotProcessing,
  setCopilotLoadingText,
  setIsWaitingForConfirmation,
  setIsWaitingInQueue,
  setQueuePosition,
  scrollToBottom,
  markAsRead,
}: UseConversationWebSocketParams) => {
  const history = useHistory();

  useWebSocket(
    {
      channel: "HelpWidget::ConversationChannel",
      onMessageReceive: (data: {
        event: string;
        message?: HelpWidgetMessage;
        conversation?: HelpWidgetConversation;
        url?: string;
        position?: number;
        agent_first_name?: string;
      }) => {
        if (data.event === "newMessage" && data.message) {
          const msg: HelpWidgetMessage = data.message;
          setMessages(prevMessages =>
            appendMessageWithFadingEffect(prevMessages, msg),
          );
          markAsRead({ conversationId });
        } else if (data.event === "redirectTo" && data.url) {
          history.push(data.url);
        } else if (data.event === "enterQueue") {
          scrollToBottom();
        } else if (data.event === "updateQueue") {
          setQueuePosition(data.position || 0);
        } else if (data.event === "updateConversation" && data.conversation) {
          setChannel(data.conversation.channel || null);
          setStatus(data.conversation.status || null);
          setIsBotProcessing(data.conversation.is_bot_processing || false);
          setCopilotLoadingText(
            data.conversation.copilot_action_loading_text || null,
          );
          setIsWaitingForConfirmation(
            data.conversation.is_waiting_for_confirmation || false,
          );
          setIsWaitingInQueue(data.conversation.is_waiting_in_queue || false);
          setQueuePosition(data.conversation.queue_position || null);
        }
      },
      conversation_id: conversationId,
      canCreateConnection: Boolean(conversationId) && !isVirtualConversation,
    },
    [conversationId, isVirtualConversation],
  );
};
